import type { SidebarMenuItem } from '@nebula-layout/layout';
import { normalizeMenuLabelKey } from './vault-menu-labels';

/** Ruta canónica en vault-shell (alineada con `vault.routes.ts`). */
const PRESERVATION_MESSAGES_VAULT_ROUTE = '/layout/vault/preservation-messages';

const ROUTE_MARKERS = ['preservation-messages', 'preservationmessages', 'preservation_messages'];

/**
 * Detecta ítem de menú hacia mensajes informativos (API puede usar path distinto al slug de la app).
 */
export function menuItemTargetsPreservationMessages(item: SidebarMenuItem): boolean {
  const r = (item.route ?? '').toLowerCase();
  if (ROUTE_MARKERS.some((m) => r.includes(m))) return true;
  const lbl = normalizeMenuLabelKey(item.label ?? '').toLowerCase();
  if (lbl === 'mensajes informativos') return true;
  if (lbl.includes('preservation') && lbl.includes('message')) return true;
  return false;
}

function menuHasPreservationMessagesItem(items: SidebarMenuItem[]): boolean {
  for (const item of items) {
    if (menuItemTargetsPreservationMessages(item)) return true;
    if (item.children?.length && menuHasPreservationMessagesItem(item.children)) return true;
  }
  return false;
}

/** ¿El snapshot del menú corresponde a la app Vault? (evita inyectar en contabilidad / otros productos). */
export function menuSnapshotLooksLikeVault(items: SidebarMenuItem[]): boolean {
  if (typeof localStorage !== 'undefined') {
    const route = localStorage.getItem('selectedRoute');
    if (route === 'vault') return true;
  }

  if (typeof window !== 'undefined') {
    const path = window.location.pathname.toLowerCase();
    if (path.includes('/layout/vault')) return true;
  }

  const walk = (list: SidebarMenuItem[]): boolean => {
    for (const it of list) {
      const r = (it.route ?? '').toLowerCase();
      if (r.includes('/layout/vault/') || r.includes('/vault/')) return true;
      if (it.children?.length && walk(it.children)) return true;
    }
    return false;
  };

  return walk(items);
}

/**
 * El menú lateral viene del API (`read-by-roles-grouped`). Si la opción no viene en la respuesta,
 * igual mostramos el acceso en **menú Vault** para que no desaparezca el módulo. La pantalla sigue
 * aplicando permisos (solo administración si corresponde).
 */
export function ensurePreservationMessagesSidebarItem(items: SidebarMenuItem[]): SidebarMenuItem[] {
  // Si el API no devolvió menú, no inventar ítems (evita sidebar con solo "Mensajes Informativos").
  if (!items.length) return items;
  if (!menuSnapshotLooksLikeVault(items)) return items;
  if (menuHasPreservationMessagesItem(items)) return items;

  const idx = findControlCenterGroupIndex(items);
  const injected: SidebarMenuItem = {
    label: 'Mensajes Informativos',
    icon: 'Info',
    route: PRESERVATION_MESSAGES_VAULT_ROUTE,
    children: [],
  };

  if (idx < 0) {
    return [...items, injected];
  }

  return items.map((item, i) => {
    if (i !== idx) return item;
    return {
      ...item,
      children: [...(item.children ?? []), injected],
    };
  });
}

function findControlCenterGroupIndex(items: SidebarMenuItem[]): number {
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    if (!it.children?.length) continue;

    const lbl = normalizeMenuLabelKey(it.label).toLowerCase();
    if (lbl === 'centro de control') return i;
    if (lbl.includes('vault system')) return i;

    const childRoutes = it.children.map((c) => (c.route ?? '').toLowerCase());
    const looksLikeControlCenter =
      childRoutes.some((r) => r.includes('/audit')) &&
      (childRoutes.some((r) => r.includes('/settings')) ||
        childRoutes.some((r) => r.includes('/notifications')));
    if (looksLikeControlCenter) return i;
  }

  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    if (!it.children?.length) continue;
    if (it.children.some((c) => (c.route ?? '').toLowerCase().includes('/audit'))) return i;
  }

  return -1;
}

/**
 * El API (`read-by-roles-grouped`) ya define qué opciones ve cada rol. No eliminar entradas en el
 * cliente: eso ocultaba módulos (p. ej. Mensajes Informativos) aunque el backend las enviara.
 */
export function filterPreservationMessagesMenuItems(items: SidebarMenuItem[]): SidebarMenuItem[] {
  return items;
}
